import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Building2, Bike, Mail, Lock, User, Phone, ArrowLeft, MapPin, Store } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { saveBusinessDraft, syncBusinessVerification } from "@/lib/businessOnboarding";
import { useAuth } from "@/context/AuthContext";
import { roleHome } from "@/lib/format";
import { Logo, LogoFull } from "@/components/Logo";

const HERO_REMOVED = true;

const inputBase =
  "w-full rounded-xl border border-[#27272A] bg-[#0d0d0f] py-3 pl-11 pr-3 text-sm text-white placeholder-gray-500 outline-none transition-colors focus:border-[#FF6A00]";

export default function Auth() {
  const nav = useNavigate();
  const { signIn, signUp, resetPassword } = useAuth();
  const [params, setParams] = useSearchParams();
  const urlMode = params.get("mode");
  const mode = ["register", "forgot"].includes(urlMode) ? urlMode : "login";
  const setMode = (m) => setParams(m === "login" ? {} : { mode: m }, { replace: true });
  const [role, setRole] = useState("BUSINESS");
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({
    email: "", password: "", full_name: "", phone: "", business_name: "",
    // Piloto BUSINESS: activity_type es solo informativo (no se persiste);
    // municipality y pickup_address quedan en el estado del formulario y en un
    // borrador local hasta que exista el flujo real de verificación (0024).
    activity_type: "", municipality: "", pickup_address: "",
  });
  const [acceptLegal, setAcceptLegal] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  // Estado de completitud del formulario (solo visual: habilita el botón y orienta
  // al usuario; las validaciones en submit se conservan como red de seguridad).
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim());
  const phoneOk = /^[0-9+\s()-]{7,15}$/.test(f.phone.trim());
  const passOk = f.password.length >= 6;
  const bizBaseOk = Boolean(f.full_name.trim() && f.business_name.trim() && f.municipality.trim() && f.pickup_address.trim());
  const canSubmit =
    mode === "forgot" ? Boolean(f.email.trim())
    : mode === "login" ? Boolean(f.email.trim() && f.password)
    : role === "BUSINESS" ? Boolean(bizBaseOk && phoneOk && emailOk && passOk && acceptLegal)
    : Boolean(f.full_name.trim() && emailOk && passOk);

  const goHome = async (uid) => {
    const { data } = await supabase.from("profiles").select("role").eq("id", uid).maybeSingle();
    nav(roleHome(data?.role || "BUSINESS"));
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const { data, error } = await signIn(f.email.trim(), f.password);
        if (error) throw error;
        toast.success("Bienvenido de nuevo");
        await goHome(data.user.id);
      } else if (mode === "register") {
        if (role === "BUSINESS") {
          if (!f.business_name.trim() || !f.full_name.trim() || !f.municipality.trim() || !f.pickup_address.trim())
            throw new Error("Completa: nombre del negocio, responsable, municipio y dirección de recogida.");
          if (!/^[0-9+\s()-]{7,15}$/.test(f.phone.trim()))
            throw new Error("Teléfono de contacto inválido (7 a 15 dígitos).");
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim()))
            throw new Error("Correo electrónico inválido.");
          if (!acceptLegal)
            throw new Error("Debes aceptar los términos y la política de privacidad para registrar el negocio.");
        }
        const meta = {
          role,
          full_name: f.full_name,
          phone: f.phone,
          ...(role === "BUSINESS" ? { business_name: f.business_name } : {}),
        };
        const { data, error } = await signUp(f.email.trim(), f.password, meta);
        if (error) throw error;
        // Piloto: solo la dirección, el municipio y la marca de aceptación legal se
        // guardan como BORRADOR LOCAL temporal (transporte mínimo, TTL 7 días, nunca
        // contraseñas ni tokens; el tipo de negocio NO se guarda) hasta sincronizarlos
        // con la RPC real upsert_my_business_verification (0024). Se elimina al
        // sincronizar, expirar, cerrar sesión o cambiar de cuenta.
        if (role === "BUSINESS") {
          saveBusinessDraft(f.email, {
            municipality: f.municipality.trim(),
            pickup_address: f.pickup_address.trim(),
            legal_accepted_at: new Date().toISOString(), // marca provisional de cliente (no es sello de servidor)
          });
        }
        if (data.session) {
          if (role === "BUSINESS") {
            const r = await syncBusinessVerification({
              email: f.email, fullName: f.full_name, phone: f.phone, businessName: f.business_name,
            });
            if (r.status === "synced") toast.success("Datos del negocio registrados para verificación.");
            else if (r.status === "error")
              toast.error("Cuenta creada, pero los datos complementarios del negocio quedaron pendientes; podrás reintentarlos desde tu panel.");
            // "pending_migration" / "none": silencio intencional (0024 aún no aplicada).
          }
          toast.success("Cuenta creada");
          await goHome(data.user.id);
        } else {
          toast.success("Cuenta creada. Revisa tu correo para confirmarla antes de ingresar.");
          setMode("login");
        }
      } else {
        const { error } = await resetPassword(f.email.trim());
        if (error) throw error;
        toast.success("Te enviamos un enlace para restablecer tu contraseña.");
        setMode("login");
      }
    } catch (err) {
      toast.error(err?.message || "Ocurrió un error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center bg-[#0B0B0B] px-5 py-8">
      <div className="w-full max-w-md">
        {/* Logo oficial: protagonista, integrado al fondo, altura contenida */}
        <div className="flex justify-center">
          <LogoFull className="h-24 w-auto sm:h-28" />
        </div>

        <Link to="/" className="mb-5 mt-2 inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-white">
          <ArrowLeft size={15} /> Volver
        </Link>

        <h1 className="font-heading text-2xl font-extrabold text-white sm:text-3xl">
          {mode === "login" ? "Inicia sesión" : mode === "register" ? "Crea tu cuenta" : "Recuperar contraseña"}
        </h1>
        <p className="mt-1 text-sm text-gray-400">
          {mode === "forgot"
            ? "Ingresa tu correo y te enviaremos un enlace."
            : "Plataforma de envíos y logística de carga."}
        </p>

          {mode === "register" && (
            <div className="mt-6 grid grid-cols-2 gap-3">
              {[
                { key: "BUSINESS", icon: Building2, label: "Negocio", desc: "Solicito envíos" },
                { key: "DRIVER", icon: Bike, label: "Conductor", desc: "Realizo entregas" },
              ].map((r) => {
                const Icon = r.icon;
                const active = role === r.key;
                return (
                  <button
                    key={r.key}
                    type="button"
                    onClick={() => setRole(r.key)}
                    data-testid={`role-select-${r.key.toLowerCase()}`}
                    className={`rounded-xl border p-3 text-left transition-all ${
                      active
                        ? "border-[#FF6A00] bg-[#FF6A00]/10"
                        : "border-[#27272A] bg-[#0d0d0f] hover:border-[#3f3f46]"
                    }`}
                  >
                    <Icon size={20} className={active ? "text-[#FF6A00]" : "text-gray-400"} />
                    <div className="mt-2 font-heading text-sm font-bold text-white">{r.label}</div>
                    <div className="text-xs text-gray-500">{r.desc}</div>
                  </button>
                );
              })}
            </div>
          )}

          <form onSubmit={submit} className="mt-5 space-y-3.5">
            {mode === "register" && (
              <>
                <Field icon={User} placeholder={role === "BUSINESS" ? "Nombre completo del responsable" : "Nombre completo"} value={f.full_name} onChange={set("full_name")} testId="auth-fullname-input" required reqStar />
                {role === "BUSINESS" && (
                  <>
                    <Field icon={Building2} placeholder="Nombre del negocio" value={f.business_name} onChange={set("business_name")} testId="auth-business-input" required reqStar />
                    <div>
                      <div className="relative">
                        <Store size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
                        <select
                          value={f.activity_type}
                          onChange={set("activity_type")}
                          data-testid="auth-activity-type-select"
                          className={`${inputBase} appearance-none ${f.activity_type ? "text-white" : "text-gray-500"}`}
                        >
                          <option value="">Tipo de negocio (informativo, no se guarda)</option>
                          <option value="ferreteria">Ferretería</option>
                          <option value="drogueria">Droguería / Farmacia</option>
                          <option value="restaurante">Restaurante / Comidas</option>
                          <option value="tienda">Tienda / Variedades</option>
                          <option value="repuestos">Repuestos / Autopartes</option>
                          <option value="otro">Otro</option>
                        </select>
                      </div>
                    </div>
                    <Field icon={MapPin} placeholder="Municipio (ej. El Cerrito, Valle del Cauca)" value={f.municipality} onChange={set("municipality")} testId="auth-municipality-input" required reqStar />
                    <Field icon={MapPin} placeholder="Dirección habitual de recogida" value={f.pickup_address} onChange={set("pickup_address")} testId="auth-pickup-address-input" required reqStar />
                    <p className="text-xs text-gray-500" data-testid="auth-business-draft-note">
                      La dirección y el municipio se envían a tu verificación al crear la cuenta; si el
                      servicio de verificación aún no está disponible, quedan como borrador temporal en
                      este dispositivo (máx. 30 días) y podrás completarlos desde tu panel.
                    </p>
                  </>
                )}
                <Field icon={Phone} placeholder={role === "BUSINESS" ? "Teléfono de contacto" : "Teléfono"} value={f.phone} onChange={set("phone")} testId="auth-phone-input" required={role === "BUSINESS"} reqStar={role === "BUSINESS"} />
                {role === "BUSINESS" && (
                  <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-[#27272A] bg-[#0d0d0f] px-3.5 py-3" data-testid="auth-legal-accept-label">
                    <input
                      type="checkbox"
                      checked={acceptLegal}
                      onChange={(e) => setAcceptLegal(e.target.checked)}
                      data-testid="auth-legal-accept-checkbox"
                      className="mt-0.5 h-4 w-4 shrink-0 accent-[#FF6A00]"
                    />
                    <span className="text-xs leading-relaxed text-gray-400">
                      Acepto los{" "}
                      <a href="/legal/terminos" target="_blank" rel="noreferrer"
                        className="inline-flex min-h-[44px] items-center align-middle -my-3 font-semibold text-[#FF6A00] hover:underline" data-testid="auth-legal-link-terminos">
                        términos y condiciones
                      </a>{" "}
                      y la{" "}
                      <a href="/legal/privacidad" target="_blank" rel="noreferrer"
                        className="inline-flex min-h-[44px] items-center align-middle -my-3 font-semibold text-[#FF6A00] hover:underline" data-testid="auth-legal-link-privacidad">
                        política de privacidad
                      </a>{" "}
                      de RAPIFAST. Ambos documentos son{" "}
                      <span className="font-semibold text-amber-400">borradores del piloto pendientes de formalización
                      y aprobación legal</span>; esta aceptación es para las pruebas internas y no constituye
                      aprobación legal definitiva. Obligatorio para registrar el negocio.
                    </span>
                  </label>
                )}
              </>
            )}
            <Field icon={Mail} type="email" placeholder="Correo electrónico" value={f.email} onChange={set("email")} testId="auth-email-input" required reqStar={mode === "register"} />
            {mode !== "forgot" && (
              <Field icon={Lock} type="password" placeholder="Contraseña" value={f.password} onChange={set("password")} testId="auth-password-input" required reqStar={mode === "register"} minLength={6} />
            )}
            {mode === "register" && (
              <p className="text-xs text-gray-500" data-testid="auth-required-note">
                <span className="font-bold text-[#FF6A00]">*</span> Campos obligatorios
                {role === "BUSINESS" ? ", incluida la aceptación de los documentos del piloto (borradores en revisión)" : ""}.
              </p>
            )}

            {mode === "login" && (
              <button
                type="button"
                onClick={() => setMode("forgot")}
                className="text-xs font-semibold text-[#FF6A00] hover:underline"
                data-testid="auth-forgot-link"
              >
                ¿Olvidaste tu contraseña?
              </button>
            )}

            <button
              type="submit"
              disabled={loading || !canSubmit}
              data-testid="auth-submit-button"
              className="w-full rounded-xl bg-[#FF6A00] py-3.5 font-extrabold text-black shadow-md shadow-[#FF6A00]/25 transition-all active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none"
            >
              {loading
                ? "Procesando…"
                : mode === "login"
                ? "Ingresar"
                : mode === "register"
                ? "Crear cuenta"
                : "Enviar enlace"}
            </button>
            {mode === "register" && !canSubmit && !loading && (
              <p className="text-center text-xs text-gray-500" data-testid="auth-submit-hint">
                Completa los campos obligatorios{role === "BUSINESS" ? " y acepta los documentos" : ""} para continuar.
              </p>
            )}
          </form>

          <div className="mt-6 text-center text-sm text-gray-400">
            {mode === "login" ? (
              <>
                ¿No tienes cuenta?{" "}
                <button onClick={() => setMode("register")} className="font-semibold text-[#FF6A00] hover:underline" data-testid="auth-goto-register">
                  Regístrate
                </button>
              </>
            ) : (
              <>
                ¿Ya tienes cuenta?{" "}
                <button onClick={() => setMode("login")} className="font-semibold text-[#FF6A00] hover:underline" data-testid="auth-goto-login">
                  Inicia sesión
                </button>
              </>
            )}
          </div>
        </div>
      </div>
  );
}

const Field = ({ icon: Icon, testId, reqStar, required, ...props }) => (
  <div className="relative">
    <Icon size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
    <input className={inputBase} data-testid={testId} required={required} {...props} />
    {reqStar && (
      <span aria-hidden="true" className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-sm font-bold text-[#FF6A00]">*</span>
    )}
  </div>
);
